class BlogGalleryRTE extends HTMLElement {
  cleanupCallbacks = [];
  trackAnimationFrame = 0;

  connectedCallback() {
    if (this.dataset.galleryReady === 'true') return;

    this.dataset.galleryReady = 'true';
    this.formatImageGroups();
  }

  disconnectedCallback() {
    this.cleanupCallbacks.forEach((callback) => callback());
    this.cleanupCallbacks = [];
  }

  formatImageGroups() {
    const children = Array.from(this.children);
    const minGallerySize = Number.parseInt(this.dataset.minGallerySize || '3', 10);

    let imageRun = [];
    let blankRun = [];
    let galleryIndex = 0;

    const flushRun = () => {
      const images = imageRun.flatMap((item) => item.images);

      if (images.length >= minGallerySize) {
        const gallery = this.buildGallery(images, galleryIndex);
        imageRun[0].element.before(gallery);

        [...imageRun.map((item) => item.element), ...blankRun].forEach((element) => element.remove());
        galleryIndex += 1;
      }

      imageRun = [];
      blankRun = [];
    };

    children.forEach((child) => {
      if (!(child instanceof HTMLElement)) {
        flushRun();
        return;
      }

      const images = this.getImageDataList(child);

      if (images.length > 0) {
        imageRun.push({ element: child, images });
        return;
      }

      if (imageRun.length > 0 && this.isBlankElement(child)) {
        blankRun.push(child);
        return;
      }

      flushRun();
    });

    flushRun();
  }

  isBlankElement(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.querySelector('img, picture, figure, video, iframe, table, blockquote, ul, ol')) return false;

    return element.textContent?.trim() === '';
  }

  getImageData(element) {
    if (!(element instanceof HTMLElement)) return null;

    const root = this.getStandaloneImageRoot(element);
    if (!root) return null;

    return this.getImageDataFromRoot(root);
  }

  getImageDataList(element) {
    const singleImage = this.getImageData(element);
    if (singleImage) return [singleImage];
    if (!this.isImageOnlyContainer(element)) return [];

    return this.getImageRoots(element).map((root) => this.getImageDataFromRoot(root)).filter(Boolean);
  }

  getImageDataFromRoot(root) {
    let link = null;
    let image = null;

    if (root instanceof HTMLImageElement) {
      image = root;
    } else if (root instanceof HTMLAnchorElement) {
      link = root;
      const child = root.querySelector('img');
      if (child instanceof HTMLImageElement) {
        image = child;
      }
    }

    if (!(image instanceof HTMLImageElement)) return null;

    const linkHref = link?.getAttribute('href') || '';
    const hasDirectImageHref = /\.(avif|gif|jpe?g|png|svg|webp)(\?.*)?$/i.test(linkHref);
    const fullImageSource = hasDirectImageHref ? link.href : image.currentSrc || image.src;
    const shopLink = link && linkHref && !hasDirectImageHref && !linkHref.startsWith('#') ? link.href : '';
    const ctaLabel = link?.getAttribute('title') || this.dataset.ctaLabel || 'Shop the look';

    return {
      alt: image.alt || '',
      ctaLabel,
      fullImageSource,
      height: image.getAttribute('height') || '',
      sizes: image.getAttribute('sizes') || '',
      shopLink,
      src: image.currentSrc || image.src,
      srcset: image.getAttribute('srcset') || '',
      width: image.getAttribute('width') || '',
    };
  }

  isImageOnlyContainer(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (!element.querySelector('img')) return false;
    if (element.textContent?.replace(/\u00a0/g, '').trim() !== '') return false;

    const allowedTags = new Set(['A', 'IMG', 'PICTURE', 'SOURCE', 'BR', 'SPAN']);

    return Array.from(element.querySelectorAll('*')).every((child) => allowedTags.has(child.tagName));
  }

  getImageRoots(element) {
    const roots = [];

    element.querySelectorAll('img').forEach((image) => {
      const link = image.closest('a');
      const root = link && element.contains(link) ? link : image;

      if (!roots.includes(root)) {
        roots.push(root);
      }
    });

    return roots;
  }

  getStandaloneImageRoot(element) {
    if (element instanceof HTMLImageElement || element instanceof HTMLAnchorElement) {
      return this.hasOnlyImageContent(element) ? element : null;
    }

    const contentChildren = Array.from(element.children).filter((child) => child.tagName !== 'BR');
    if (contentChildren.length !== 1) return null;
    if (element.textContent?.trim() !== '') return null;

    const [onlyChild] = contentChildren;
    if (!(onlyChild instanceof HTMLElement)) return null;

    return this.hasOnlyImageContent(onlyChild) ? onlyChild : null;
  }

  hasOnlyImageContent(element) {
    if (element instanceof HTMLImageElement) return true;

    if (element instanceof HTMLAnchorElement) {
      return element.querySelectorAll('img').length === 1 && element.textContent?.trim() === '';
    }

    return false;
  }

  buildGallery(images, galleryIndex) {
    const columns = Number.parseInt(this.dataset.columns || '3', 10);
    const gap = this.dataset.gap || '16';

    const wrapper = document.createElement('section');
    wrapper.className = 'blog-gallery-rte__gallery';
    wrapper.style.setProperty('--blog-gallery-columns', `${columns}`);
    wrapper.style.setProperty('--blog-gallery-gap', `${gap}px`);

    const grid = document.createElement('div');
    grid.className = 'blog-gallery-rte__grid';

    const dialog = document.createElement('dialog');
    dialog.className = 'blog-gallery-rte__dialog';
    dialog.setAttribute('scroll-lock', '');
    dialog.setAttribute('aria-label', `Image gallery ${galleryIndex + 1}`);

    const viewer = document.createElement('div');
    viewer.className = 'blog-gallery-rte__viewer';

    const toolbar = document.createElement('div');
    toolbar.className = 'blog-gallery-rte__toolbar';

    const counter = document.createElement('div');
    counter.className = 'visually-hidden blog-gallery-rte__counter';
    counter.setAttribute('aria-live', 'polite');

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'blog-gallery-rte__close';
    closeButton.textContent = '×';
    closeButton.setAttribute('aria-label', 'Close image gallery');

    const previousButton = document.createElement('button');
    previousButton.type = 'button';
    previousButton.className = 'blog-gallery-rte__nav blog-gallery-rte__nav--prev';
    previousButton.textContent = '<';
    previousButton.setAttribute('aria-label', 'Previous image');

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'blog-gallery-rte__nav blog-gallery-rte__nav--next';
    nextButton.textContent = '>';
    nextButton.setAttribute('aria-label', 'Next image');

    const track = document.createElement('div');
    track.className = 'blog-gallery-rte__track';

    const dots = document.createElement('div');
    dots.className = 'blog-gallery-rte__dots';

    const state = { activeIndex: 0 };
    const dotButtons = [];

    const updateControls = () => {
      counter.textContent = `${state.activeIndex + 1} / ${images.length}`;
      previousButton.disabled = state.activeIndex <= 0;
      nextButton.disabled = state.activeIndex >= images.length - 1;

      dotButtons.forEach(({ button, index }) => {
        button.setAttribute('aria-selected', `${index === state.activeIndex}`);
      });
    };

    const scrollToIndex = (index, behavior = 'smooth') => {
      const safeIndex = Math.max(0, Math.min(index, images.length - 1));
      state.activeIndex = safeIndex;
      const targetLeft = track.clientWidth * safeIndex;

      if (behavior === 'auto') {
        track.scrollTo({ left: targetLeft, behavior });
      } else {
        this.smoothScrollTrack(track, targetLeft);
      }

      updateControls();
    };

    const openGallery = (index) => {
      state.activeIndex = index;
      dialog.showModal();

      requestAnimationFrame(() => {
        scrollToIndex(index, 'auto');
      });
    };

    let scrollTimeout = 0;

    const syncActiveIndexFromScroll = () => {
      window.clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => {
        const nextIndex = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        if (nextIndex !== state.activeIndex) {
          state.activeIndex = nextIndex;
          updateControls();
        }
      }, 80);
    };

    closeButton.addEventListener('click', () => dialog.close());
    previousButton.addEventListener('click', () => scrollToIndex(state.activeIndex - 1));
    nextButton.addEventListener('click', () => scrollToIndex(state.activeIndex + 1));
    track.addEventListener('scroll', syncActiveIndexFromScroll, { passive: true });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        dialog.close();
      }
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollToIndex(state.activeIndex - 1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollToIndex(state.activeIndex + 1);
      }
    });

    const handleResize = () => {
      if (dialog.open) {
        scrollToIndex(state.activeIndex, 'auto');
      }
    };

    window.addEventListener('resize', handleResize);
    this.cleanupCallbacks.push(() => window.removeEventListener('resize', handleResize));

    images.forEach((imageData, index) => {
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'blog-gallery-rte__trigger';
      trigger.setAttribute('aria-label', `Open image ${index + 1} of ${images.length}`);

      const triggerImage = this.buildImage(imageData, 'blog-gallery-rte__trigger-image');
      trigger.append(triggerImage);
      trigger.addEventListener('click', () => openGallery(index));
      grid.append(trigger);

      const slide = document.createElement('div');
      slide.className = 'blog-gallery-rte__slide';

      const figure = document.createElement('div');
      figure.className = 'blog-gallery-rte__figure';

      const imageShell = document.createElement('div');
      imageShell.className = 'blog-gallery-rte__image-shell';

      const slideImage = this.buildImage(
        {
          ...imageData,
          src: imageData.fullImageSource || imageData.src,
          srcset: '',
          sizes: '',
        },
        'blog-gallery-rte__slide-image'
      );

      imageShell.append(slideImage);
      figure.append(imageShell);

      if (imageData.shopLink) {
        const cta = document.createElement('a');
        cta.className = 'blog-gallery-rte__cta';
        cta.href = imageData.shopLink;
        cta.textContent = imageData.ctaLabel;
        figure.append(cta);
      }

      slide.append(figure);
      track.append(slide);
    });

    images.forEach((_image, dotIndex) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'blog-gallery-rte__dot';
      dot.setAttribute('aria-label', `View image ${dotIndex + 1} of ${images.length}`);
      dot.addEventListener('click', () => scrollToIndex(dotIndex));

      dots.append(dot);
      dotButtons.push({ button: dot, index: dotIndex });
    });

    updateControls();

    toolbar.append(counter, closeButton);
    viewer.append(toolbar, previousButton, track, nextButton, dots);
    dialog.append(viewer);
    wrapper.append(grid, dialog);

    return wrapper;
  }

  smoothScrollTrack(track, targetLeft) {
    cancelAnimationFrame(this.trackAnimationFrame);

    const startLeft = track.scrollLeft;
    const distance = targetLeft - startLeft;
    const duration = 520;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = Math.min((currentTime - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - elapsed, 3);

      track.scrollLeft = startLeft + distance * eased;

      if (elapsed < 1) {
        this.trackAnimationFrame = requestAnimationFrame(animate);
      }
    };

    this.trackAnimationFrame = requestAnimationFrame(animate);
  }

  buildImage(imageData, className) {
    const image = document.createElement('img');
    image.className = className;
    image.src = imageData.src;
    image.alt = imageData.alt;
    image.loading = 'lazy';

    if (imageData.srcset) image.srcset = imageData.srcset;
    if (imageData.sizes) image.sizes = imageData.sizes;
    if (imageData.width) image.width = Number.parseInt(imageData.width, 10);
    if (imageData.height) image.height = Number.parseInt(imageData.height, 10);

    return image;
  }
}

if (!customElements.get('blog-gallery-rte')) {
  customElements.define('blog-gallery-rte', BlogGalleryRTE);
}
